import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { formatRequestTimestamp } from "../../utils/formatDateTime";

export default function StudentDashboard() {
  const [user, setUser] = useState(null);
  const [research, setResearch] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("browse");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResearch, setSelectedResearch] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestReason, setRequestReason] = useState("");
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState(false);
  const [abstractExpandedBrowse, setAbstractExpandedBrowse] = useState({});
  const navigate = useNavigate();

  const ABSTRACT_PREVIEW_CHARS = 150;

  const toggleBrowseAbstract = (id) => {
    setAbstractExpandedBrowse((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const loadDashboardData = useCallback(async () => {
    if (!user?._id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [researchRes, requestsRes] = await Promise.all([
        api.get("/research"),
        api.get("/request")
      ]);
      
      setResearch(researchRes.data.research || researchRes.data || []);
      
      // Filter requests for current user - handle both string and ObjectId formats
      const userRequests = requestsRes.data.requests?.filter(req => {
        const reqStudentId = req.studentId?._id || req.studentId;
        const currentUserId = user._id;
        return reqStudentId === currentUserId;
      }) || [];
      setMyRequests(userRequests);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [user?._id]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error("Error parsing user data:", error);
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (user?._id) {
      loadDashboardData();
    }
  }, [user, loadDashboardData]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleRequestAccess = (research) => {
    setSelectedResearch(research);
    setShowRequestModal(true);
  };

  const handleViewDocument = async (research) => {
    try {
      setDownloadingDocument(true);
      setError(null);
      
      // Create a temporary link to download the file
      const response = await api.get(`/research/download/${research._id}`, {
        responseType: 'blob'
      });
      
      // Create blob URL
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = research.pdfFilename || `${research.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      setSuccessMessage("Document downloaded successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error downloading document:", error);
      if (error.response?.status === 403) {
        setError("Access denied. You need approval to view this document.");
      } else {
        setError("Failed to download document. Please try again.");
      }
    } finally {
      setDownloadingDocument(false);
    }
  };

  const submitRequest = async () => {
    if (!requestReason.trim()) return;

    setSubmittingRequest(true);
    setError(null); // Clear any existing errors
    
    try {
      const response = await api.post("/request", {
        researchId: selectedResearch._id,
        reason: requestReason
      });

      setShowRequestModal(false);
      setRequestReason("");
      setSelectedResearch(null);
      
      // Show success message
      setSuccessMessage("Request submitted successfully! Please wait for admin approval.");
      setTimeout(() => setSuccessMessage(null), 5000);
      
      loadDashboardData(); // Refresh requests
    } catch (error) {
      console.error("Error submitting request:", error);
      
      // Provide more specific error messages
      if (error.response?.status === 400) {
        setError(error.response.data.error || "Invalid request. Please check your input.");
      } else if (error.response?.status === 401) {
        setError("Session expired. Please log in again.");
      } else if (error.response?.status === 403) {
        setError("Access denied. You don't have permission to submit requests.");
      } else if (error.response?.status === 404) {
        setError("Research not found. Please try again.");
      } else if (error.response?.status === 409) {
        setError("You already have a pending request for this research.");
      } else {
        setError("Failed to submit request. Please try again.");
      }
    } finally {
      setSubmittingRequest(false);
    }
  };

  const filteredResearch = research.filter(doc => 
    doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.authors?.some(author => author.toLowerCase().includes(searchTerm.toLowerCase())) ||
    doc.keywords?.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const renderStudentBrowseAbstract = (doc) => {
    if (!doc.abstract) {
      return <p>No abstract available.</p>;
    }
    const full = doc.abstract;
    const long = full.length > ABSTRACT_PREVIEW_CHARS;
    const expanded = Boolean(abstractExpandedBrowse[doc._id]);
    return (
      <>
        <p className="whitespace-pre-wrap break-words">
          {long && !expanded ? `${full.slice(0, ABSTRACT_PREVIEW_CHARS)}…` : full}
        </p>
        {long && (
          <button
            type="button"
            onClick={() => toggleBrowseAbstract(doc._id)}
            className="mt-2 text-sm font-semibold text-blue-400 hover:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
          >
            {expanded ? "View less" : "View more"}
          </button>
        )}
      </>
    );
  };

  const renderBrowseResearch = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search research by title, authors, or keywords..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 rounded bg-gray-700 text-white"
        />
      </div>
      
      <div className="mb-4 p-3 bg-blue-900 border border-blue-700 rounded-lg">
        <p className="text-sm text-blue-200">
          <strong>📋 Access Control:</strong> All research documents require admin approval before access. 
          Click "Request Access" on any research to submit your request for review.
        </p>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-600 text-white rounded">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 p-3 bg-green-600 text-white rounded">
          {successMessage}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredResearch.length === 0 ? (
          <div className="col-span-full text-center text-gray-400 py-8">
            {searchTerm ? "No research found matching your search." : "No research available."}
          </div>
        ) : (
          filteredResearch.map((doc) => (
            <div key={doc._id} className="bg-gray-700 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">{doc.title || "Untitled"}</h4>
              <p className="text-sm text-gray-300 mb-2">
                Authors: {doc.authors?.join(", ") || "Unknown"}
              </p>
              <p className="text-sm text-gray-300 mb-2">
                Year: {doc.year || "N/A"}
              </p>
              <p className="text-sm text-gray-300 mb-2">
                Campus: {doc.campus || "N/A"}
              </p>
              <p className="text-sm text-gray-300 mb-2">
                Keywords: {doc.keywords?.join(", ") || "None"}
              </p>
              <div className="text-sm text-gray-300 mb-4">
                {renderStudentBrowseAbstract(doc)}
              </div>
              
              {/* All research requires request access first */}
              {myRequests.some(req => {
                const reqResearchId = req.researchId?._id || req.researchId;
                const docId = doc._id;
                return reqResearchId === docId;
              }) ? (
                <div className="text-center">
                  <span className="text-yellow-400 text-sm">
                    Request {myRequests.find(req => {
                      const reqResearchId = req.researchId?._id || req.researchId;
                      const docId = doc._id;
                      return reqResearchId === docId;
                    })?.status}
                  </span>
                  {myRequests.find(req => {
                    const reqResearchId = req.researchId?._id || req.researchId;
                    const docId = doc._id;
                    return reqResearchId === docId;
                  })?.status === "approved" && (
                    <button 
                      onClick={() => handleViewDocument(doc)}
                      disabled={downloadingDocument}
                      className="w-full mt-2 bg-green-600 px-3 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingDocument ? "Downloading..." : "View Document"}
                    </button>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => handleRequestAccess(doc)}
                  className="w-full bg-blue-600 px-3 py-2 rounded text-sm hover:bg-blue-700"
                >
                  Request Access
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderMyRequests = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h3 className="text-xl font-semibold mb-4">My Access Requests</h3>
      <p className="text-sm text-gray-300 mb-4">
        All research document access requires admin approval. You can only view documents after your request is approved.
      </p>
      <div className="space-y-4">
        {myRequests.length === 0 ? (
          <p className="text-gray-400">No requests found.</p>
        ) : (
          myRequests.map((request) => (
            <div key={request._id} className="bg-gray-700 p-4 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">{request.researchId?.title || "Unknown Research"}</p>
                  <p className="text-sm text-gray-300">
                    Authors: {request.researchId?.authors?.join(", ") || "Unknown"}
                  </p>
                  <p className="text-sm text-gray-300">
                    Reason: {request.reason || "No reason provided"}
                  </p>
                  <p className="text-sm text-gray-300">
                    <span className="text-gray-500">Requested:</span>{" "}
                    {formatRequestTimestamp(request.dateRequested)}
                  </p>
                  {request.dateProcessed && (
                    <p className="text-sm text-gray-300">
                      <span className="text-gray-500">Processed:</span>{" "}
                      {formatRequestTimestamp(request.dateProcessed)}
                    </p>
                  )}
                  {request.processedBy && (
                    <p className="text-sm text-gray-300">
                      Processed by: {request.processedBy?.name || "Unknown"}
                    </p>
                  )}
                  {request.adminComments && (
                    <p className="text-sm text-gray-300">
                      Comments: {request.adminComments}
                    </p>
                  )}
                </div>
                <div>
                  <span className={`px-3 py-1 rounded text-sm font-medium ${
                    request.status === 'approved' ? 'bg-green-600' :
                    request.status === 'declined' ? 'bg-red-600' : 'bg-yellow-600'
                  }`}>
                    {request.status === 'approved' ? '✅ Approved' :
                     request.status === 'declined' ? '❌ Declined' : '⏳ Pending'}
                  </span>
                </div>
              </div>
              {request.status === "approved" && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <p className="text-sm text-green-400 mb-2">✅ Access granted! You can now view this document.</p>
                  <button 
                    onClick={() => handleViewDocument(request.researchId)}
                    disabled={downloadingDocument}
                    className="bg-green-600 px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloadingDocument ? "Downloading..." : "View Document"}
                  </button>
                </div>
              )}
              {request.status === "declined" && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <p className="text-sm text-red-400">❌ Access denied. Please contact the administrator if you have questions.</p>
                </div>
              )}
              {request.status === "pending" && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <p className="text-sm text-yellow-400">⏳ Your request is being reviewed by an administrator.</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h3 className="text-xl font-semibold mb-4">My Profile</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Name</label>
          <p className="text-gray-300">{user?.name || "N/A"}</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <p className="text-gray-300">{user?.email || "N/A"}</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Student ID</label>
          <p className="text-gray-300">{user?.studentId || "N/A"}</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Campus</label>
          <p className="text-gray-300">{user?.campus || "N/A"}</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Role</label>
          <p className="text-gray-300 capitalize">{user?.role || "N/A"}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-blue-950">
        <div className="text-xl text-white">
          {error ? (
            <span className="bg-red-700 text-white px-4 py-2 rounded">{error}</span>
          ) : (
            "Loading dashboard..."
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-950">
      {/* Header */}
      <header className="bg-blue-900 p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">
            <span className="font-extrabold">SKSU Digital Research Hub</span> &amp; Open Access Journal - Student Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-white">Welcome, {user?.name || "Student"}</span>
            <button
              onClick={handleLogout}
              className="bg-red-600 px-4 py-2 rounded hover:bg-red-700 text-white"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-blue-800 p-4">
        <div className="flex gap-4">
          {[
            { id: "browse", label: "Browse Research" },
            { id: "requests", label: "My Requests" },
            { id: "profile", label: "Profile" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setError(null);
                setSuccessMessage(null);
              }}
              className={`px-4 py-2 rounded ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white"
                  : "bg-blue-700 text-blue-100 hover:bg-blue-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-6">
        {activeTab === "browse" && renderBrowseResearch()}
        {activeTab === "requests" && renderMyRequests()}
        {activeTab === "profile" && renderProfile()}
      </main>

      {/* Request Access Modal */}
      {showRequestModal && selectedResearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-white">Request Access</h2>
            <div className="mb-4">
              <p className="text-sm text-gray-300 mb-2">
                <strong>Research:</strong> {selectedResearch.title || "Untitled"}
              </p>
              <p className="text-sm text-gray-300 mb-2">
                <strong>Authors:</strong> {selectedResearch.authors?.join(", ") || "Unknown"}
              </p>
              <p className="text-sm text-gray-300 mb-2">
                <strong>Year:</strong> {selectedResearch.year || "N/A"}
              </p>
              <p className="text-sm text-gray-300 mb-2">
                <strong>Campus:</strong> {selectedResearch.campus || "N/A"}
              </p>
              <p className="text-xs text-yellow-400 mb-3">
                ⚠️ All research access requires admin approval before documents can be viewed.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-white">Reason for Access *</label>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                className="w-full p-2 rounded bg-gray-700 text-white h-24"
                placeholder="Please explain why you need access to this research document..."
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={submitRequest}
                disabled={submittingRequest || !requestReason.trim()}
                className="flex-1 bg-blue-600 px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 text-white"
              >
                {submittingRequest ? "Submitting..." : "Submit Request"}
              </button>
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setRequestReason("");
                  setSelectedResearch(null);
                }}
                className="flex-1 bg-gray-600 px-4 py-2 rounded hover:bg-gray-700 text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 